import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { Step } from 'app/modules/RestorePassword/types';

import { Button, ButtonIntent, ButtonSize, ButtonType } from 'app/design-system/components/Button';
import { Icon } from 'app/design-system/components/Icon';
import { useTranslation } from 'app/lang';
import { openLoginDialog } from 'app/modules/Signing/actions';

import dictionary from './SubmitFooterContent.dictionary.json';

import styles from './SubmitFooterContent.module.scss';

type SubmitFooterContentProps = {
  activeStep: Step;
  disabled: boolean;
  checked: boolean;
  backwardStep: (step: Step) => void;
};

export const SubmitFooterContent: React.FC<SubmitFooterContentProps> = ({
  activeStep,
  disabled,
  checked,
  backwardStep,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();

  const lastStepOnClick = useCallback(() => {
    dispatch(openLoginDialog());
  }, []);

  const backwardInitialButtonHandler = useCallback(() => history.replace({ search: 'signing=login' }), []);

  const backwardCodeButtonHandler = useCallback(() => backwardStep(Step.CODE), []);

  const iconContainerClassNames = classNames({
    [styles.iconContainer]: activeStep === Step.PASSWORD,
  });

  const buttonProps = {
    type: ButtonType.SUBMIT,
    size: ButtonSize.DEFAULT,
    className: styles.footerButton,
    intent: ButtonIntent.CALL_TO_ACTION,
  };
  const isLoading = disabled && !checked;

  switch (activeStep) {
    case Step.INITIAL:
      return (
        <>
          <Button data-test="modal-restorePasswordInitialButton"
                  {...buttonProps}
                  block
                  form={activeStep.toString()}
                  loading={isLoading}>
            {t(dictionary.next)}
          </Button>

          <Button type={ButtonType.BUTTON}
                  intent={ButtonIntent.DEFAULT}
                  onClick={backwardInitialButtonHandler}
                  disabled={disabled}
                  block
                  data-test="modal-restorePasswordInitialCancel">
            {t(dictionary.back)}
          </Button>
        </>
      );
    case Step.CODE:
      return (
        <>
          {checked ? (
            <Button {...buttonProps}
                    block
                    intent={ButtonIntent.SUCCESS}
                    loading={isLoading}>
              <span className={iconContainerClassNames}>
                <Icon icon="check"
                      size={14}
                      className={styles.iconSuccess} />
              </span>
            </Button>
          ) : (
            <Button data-test="modal-restorePasswordCodeButton"
                    {...buttonProps}
                    block
                    loading={isLoading}>
              {t(dictionary.next)}
            </Button>
          )}

          <Button type={ButtonType.BUTTON}
                  intent={ButtonIntent.DEFAULT}
                  onClick={backwardCodeButtonHandler}
                  disabled={disabled}
                  block
                  data-test="modal-restorePasswordCodeCancel">
            {t(dictionary.back)}
          </Button>
        </>
      );
    case Step.PASSWORD:
      return (
        <Button {...buttonProps}
                form={activeStep.toString()}
                block
                loading={isLoading}>
          {t(dictionary.save)}
        </Button>
      );
    case Step.DONE:
      return (
        <Button {...buttonProps}
                block
                onClick={lastStepOnClick}>
          {t(dictionary.login)}
        </Button>
      );
  }
};
